import React from 'react'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Grid from 'components_new/atoms/Grid'

import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsEditItem from 'components_new/molecules/SettingsItem/SettingsEditItem'
import SettingsToggleOption from 'components_new/molecules/SettingsToggleOption'

import {
  useTagOptions,
  isLoading,
  ITagStatusData,
  translateSeverity,
  translateStatusCondition,
  translateStatusDateValue,
  translateStatusValueLabel
} from './utils'
import {
  TagStatusCondition,
  TagStatusDateValue,
  TagStatusItem,
  TagStatusSeverity,
  WidgetTagObject,
  WidgetTagStatusPatchAttributes
} from 'types/GlobalWidgetTag'
import { DatasetType } from 'types/GlobalWidget'
import { useTagOptionsFetched } from 'redux/hooks/TagOptions'

interface EditProps {
  deleteWidgetTagStatus: (id: string, tagId: string) => void
  onBack: () => void
  onClose: () => void
  open: boolean
  statusItem: TagStatusItem
  tag: WidgetTagObject
  updateWidgetTagStatus: (
    id: string,
    body: WidgetTagStatusPatchAttributes,
    tagId: string
  ) => void
}

const Edit = (props: EditProps) => {
  const {
    deleteWidgetTagStatus,
    onBack,
    onClose,
    open,
    statusItem,
    tag,
    updateWidgetTagStatus
  } = props

  const tagOptionsFetched = useTagOptionsFetched()

  const options = useTagOptions(tag)
  const loading = isLoading(tag, tagOptionsFetched)
  const { t } = useTranslation()

  const getValue = (): number | string | TagStatusDateValue => {
    if (typeof statusItem.value === 'object') {
      return statusItem.value.selected
    } else {
      return statusItem.value
    }
  }

  const handleUpdate = (body: Partial<ITagStatusData>) => {
    if ('value' in body && (body.value === null || body.value === '')) {
      // do nothing
      return
    }

    updateWidgetTagStatus(
      statusItem.id,
      { data: body } as WidgetTagStatusPatchAttributes,
      tag.id
    )
  }

  const handleDelete = () => {
    deleteWidgetTagStatus(statusItem.id, tag.id)

    onBack()
  }

  return (
    <>
      {/*-- drawer --*/}
      <SettingsDrawer
        open={open}
        onBack={onBack}
        onClose={onClose}
        title={t('widgets', 'edit_indicator')}
      >
        {statusItem ? (
          <>
            <SettingsGroup helperText={t('widgets', 'set_condition_helper_text')}>
              {/*-- condition --*/}
              <SettingsEditItem<TagStatusCondition>
                input={{
                  options: statusItem.condition.options.map((option) => {
                    return {
                      title: translateStatusCondition[option][statusItem.type],
                      id: option
                    }
                  }),
                  type: 'select',
                  value: statusItem.condition.selected
                }}
                onSubmit={(value) => handleUpdate({ condition: value })}
                title={translateStatusValueLabel[statusItem.type]}
                renderValue={(value) =>
                  translateStatusCondition[value][statusItem.type]
                }
              />

              {/*-- conditional value --*/}
              {statusItem.type === DatasetType.DATE &&
              typeof statusItem.value === 'object' &&
              (statusItem.value.selected === TagStatusDateValue.DAYS_FROM_NOW ||
                statusItem.value.selected ===
                  TagStatusDateValue.DAYS_SINCE_NOW) ? (
                    <SettingsEditItem<number | null>
                      input={{
                        type: 'number',
                        value: statusItem.conditional_value
                      }}
                      onSubmit={(value) => {
                        handleUpdate({ conditional_value: value })
                      }}
                      renderValue={(value) => value?.toString() ?? ''}
                      unit={t('widgets', 'days')}
                    />
                  ) : null}

              {/*-- value --*/}
              <SettingsEditItem<number | string | TagStatusDateValue>
                input={{
                  options:
                    typeof statusItem.value === 'object'
                      ? options.map((option) => ({
                        title:
                            statusItem.type === DatasetType.DATE
                              ? translateStatusDateValue[
                                  option as TagStatusDateValue
                              ]
                              : option,
                        id: option
                      }))
                      : undefined,
                  type:
                    statusItem.type === DatasetType.DATE ||
                    statusItem.type === DatasetType.ENUM
                      ? 'select'
                      : 'text',
                  value: getValue()
                }}
                loading={loading}
                onSubmit={(value) => handleUpdate({ value: value })}
                renderValue={(value) => {
                  if (statusItem.type === DatasetType.DATE) {
                    return translateStatusDateValue[value as TagStatusDateValue]
                  } else {
                    return value?.toString() ?? ''
                  }
                }}
              />
            </SettingsGroup>

            {/*-- severity --*/}
            <SettingsGroup helperText={t('widgets', 'select_indicator_helper_text')}>
              <SettingsItem>
                <Grid container={true} columns={3}>
                  {Object.values(TagStatusSeverity).map((severity) => (
                    <Grid item={true} key={severity} xs={1}>
                      <SettingsToggleOption
                        onClick={() => {
                          if (severity !== statusItem.severity.selected) {
                            handleUpdate({ severity: severity })
                          }
                        }}
                        selected={severity === statusItem.severity.selected}
                        title={translateSeverity[severity]}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            bgcolor: `${severity}.main`,
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 240ms ease 0s',
                            '&:hover': {
                              borderColor: 'action.focus'
                            }
                          }}
                        />
                      </SettingsToggleOption>
                    </Grid>
                  ))}
                </Grid>
              </SettingsItem>
            </SettingsGroup>

            <SettingsGroup>
              <SettingsItem
                color="error"
                onClick={handleDelete}
                title={t('global', 'delete')}
                variant="action"
              />
            </SettingsGroup>
          </>
        ) : null}
      </SettingsDrawer>
    </>
  )
}

export default Edit
